/**
 * Home.jsx
 * 
 * Main student dashboard for taking quizzes and viewing progress.
 * Features:
 * - House-based access control (must be sorted to take quizzes)
 * - Interactive quiz interface with card-style answer selection
 * - Real-time score tracking and submission management
 * - Collapsible quiz sections with smooth animations
 * - Logout functionality with confirmation
 */

import { useEffect, useState, useRef } from 'react';
import { alertSuccess } from '../utils/alerts';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../App.jsx';
import Swal from 'sweetalert2';

/**
 * Animated collapsible container for quiz content
 * Provides smooth expand/collapse transitions with proper height calculation
 */
function CollapsibleQuizSection({ isExpanded, children }) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(isExpanded ? 'none' : '0px');
  const previousExpandedState = useRef(isExpanded);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const ANIMATION_BUFFER = 18; // Extra space to prevent clipping
    const measureContentHeight = () => contentElement.scrollHeight + ANIMATION_BUFFER;

    // Handle window resize events
    const handleResize = () => {
      if (isExpanded) {
        setMaxHeight(`${measureContentHeight()}px`);
      }
    };

    window.addEventListener('resize', handleResize);

    // Animate only when expanded state changes
    let animationTimeout = null;
    if (previousExpandedState.current !== isExpanded) {
      if (isExpanded) {
        // Expanding: start from 0 and animate to content height
        const targetHeight = measureContentHeight();
        setMaxHeight('0px');
        setTimeout(() => {
          setMaxHeight(`${targetHeight}px`);
          // Set to 'none' after animation completes for better performance
          animationTimeout = setTimeout(() => setMaxHeight('none'), 300);
        }, 0);
      } else {
        // Collapsing: measure current height and animate to 0
        const currentHeight = measureContentHeight();
        setMaxHeight(`${currentHeight}px`);
        setTimeout(() => setMaxHeight('0px'), 0);
      }
      previousExpandedState.current = isExpanded;
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationTimeout) clearTimeout(animationTimeout);
    };
  }, [isExpanded]);

  return (
    <div
      ref={containerRef}
      style={{
        overflow: maxHeight === 'none' ? 'visible' : 'hidden',
        transition: 'max-height 300ms ease',
        maxHeight: maxHeight,
        willChange: 'max-height',
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  
  // State for user and school data
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [schoolInfo, setSchoolInfo] = useState({ id: '', name: '' });
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [availableWordScrambles, setAvailableWordScrambles] = useState([]);
  
  // State for quiz interactions
  const [expandedQuizId, setExpandedQuizId] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizSubmissions, setQuizSubmissions] = useState({});
  
  // State for additional activities
  const [completedActivities, setCompletedActivities] = useState(new Set());
  
  // State for word scrambles
  const [completedScrambles, setCompletedScrambles] = useState(new Set());
  const [scrambleAnswers, setScrambleAnswers] = useState({});

  /**
   * Get current user data from localStorage with fallback
   */
  const getCurrentUserData = () => {
    let userData = null;
    
    // Check localStorage first, then sessionStorage
    if (window.localStorage?.getItem('hp_user')) {
      try {
        userData = JSON.parse(window.localStorage.getItem('hp_user'));
      } catch (error) {
        console.error('Failed to parse user data from localStorage:', error);
      }
    } else if (window.sessionStorage?.getItem('hp_user')) {
      try {
        userData = JSON.parse(window.sessionStorage.getItem('hp_user'));
      } catch (error) {
        console.error('Failed to parse user data from sessionStorage:', error);
      }
    }
    
    return userData || getCurrentUser();
  };

  /**
   * Fetch all necessary data from the server
   */
  const fetchHomeData = async () => {
    const user = getCurrentUserData();
    setCurrentUser(user);
    
    if (!user?.code) return;

    try {
      // Fetch school information
      const schoolResponse = await fetch(`/api/school/${user.code}`);
      const school = await schoolResponse.json();
      setSchoolInfo(school?.name ? school : { id: user.code, name: 'No school found' });

      // Fetch available quizzes
      const quizzesResponse = await fetch(`/api/quizzes/${user.code}`);
      const quizzes = await quizzesResponse.json();
      setAvailableQuizzes(Array.isArray(quizzes) ? quizzes : []);

      // Fetch available activities
      const activitiesResponse = await fetch(`/api/activities/${user.code}`);
      const activities = await activitiesResponse.json();
      setAvailableActivities(Array.isArray(activities) ? activities : []);

      // Fetch activity completions for current user
      const completionsResponse = await fetch(`/api/activity-completions/${user.code}/${user.id}`);
      const completions = await completionsResponse.json();
      if (Array.isArray(completions)) {
        const completedIds = new Set(completions.map(c => c.activityId));
        setCompletedActivities(completedIds);
      }

      // Fetch word scrambles
      const scramblesResponse = await fetch(`/api/word-scrambles/${user.code}`);
      const scrambles = await scramblesResponse.json();
      setAvailableWordScrambles(Array.isArray(scrambles) ? scrambles : []);

      // Fetch word scramble completions for current user
      const scrambleCompletionsResponse = await fetch(`/api/word-scramble-completions/${user.code}/${user.id}`);
      const scrambleCompletions = await scrambleCompletionsResponse.json();
      if (Array.isArray(scrambleCompletions)) {
        const completedScrambleIds = new Set(scrambleCompletions.map(c => c.scrambleId));
        setCompletedScrambles(completedScrambleIds);
      }

      // Fetch user's quiz submissions
      const submissionsResponse = await fetch(`/api/submissions/${user.code}`);
      const allSubmissions = await submissionsResponse.json();

      // Filter submissions for current user
      const userSubmissions = {};
      if (Array.isArray(allSubmissions)) {
        allSubmissions
          .filter(submission => submission.studentId === user.id)
          .forEach(submission => {
            userSubmissions[submission.quizId] = {
              answers: JSON.parse(submission.answers),
              score: submission.score,
            };
          });
      }
      setQuizSubmissions(userSubmissions);

      // Sync user points from server if available
      try {
        const studentsResponse = await fetch(`/api/students/${user.code}`);
        const students = await studentsResponse.json();
        if (Array.isArray(students)) {
          const currentStudent = students.find(student => student.id === user.id);
          if (currentStudent) {
            const storedUser = localStorage.getItem('hp_user');
            if (storedUser) {
              try {
                const userData = JSON.parse(storedUser);
                userData.points = typeof currentStudent.points === 'number' ? currentStudent.points : userData.points;
                userData.bonusPoints = typeof currentStudent.bonusPoints === 'number' ? currentStudent.bonusPoints : (userData.bonusPoints || 0);
                localStorage.setItem('hp_user', JSON.stringify(userData));
                setCurrentUser(userData);
              } catch (error) {
                console.error('Failed to update local user data:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync user points:', error);
      }

    } catch (error) {
      console.error('Failed to fetch home data:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchHomeData();
  }, [currentUser?.code, currentUser?.id]);

  /**
   * Handle answer selection for quiz questions
   */
  const selectQuizAnswer = (quizId, questionIndex, selectedOptionIndex) => {
    // Prevent changes if quiz is already submitted
    if (quizSubmissions[quizId]) return;
    
    setSelectedAnswers(previous => ({
      ...previous,
      [quizId]: {
        ...(previous[quizId] || {}),
        [questionIndex]: selectedOptionIndex
      }
    }));
  };

  /**
   * Submit a completed quiz and calculate score
   */
  const submitQuiz = async (quiz) => {
    const userAnswers = selectedAnswers[quiz.id] || {};
    
    // Validate that all questions have been answered
    const unansweredQuestions = quiz.questions.filter((_, index) => 
      typeof userAnswers[index] === 'undefined'
    );
    
    if (unansweredQuestions.length > 0) {
      alert(`Please answer all questions before submitting. ${unansweredQuestions.length} question(s) remaining.`);
      return;
    }
    
    // Calculate total score
    const totalScore = quiz.questions.reduce((score, question, index) => {
      return userAnswers[index] === question.correctIndex 
        ? score + (question.points || 0) 
        : score;
    }, 0);

    // Prepare answers array for server
    const answersArray = quiz.questions.map((_, index) => 
      typeof userAnswers[index] !== 'undefined' ? userAnswers[index] : null
    );

    try {
      // Submit to server
      await fetch(`/api/submissions/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissions: [{
            studentId: currentUser.id,
            quizId: quiz.id,
            answers: answersArray,
            score: totalScore,
          }],
        }),
      });

      // Update local user points
      const storedUser = localStorage.getItem('hp_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userData.points = (userData.points || 0) + totalScore;
          localStorage.setItem('hp_user', JSON.stringify(userData));
          setCurrentUser(userData);
        } catch (error) {
          console.error('Failed to update local user points:', error);
        }
      }

      // Update local submission state
      setQuizSubmissions(previous => ({
        ...previous,
        [quiz.id]: { answers: userAnswers, score: totalScore }
      }));

      // Show success message and keep quiz expanded for review
      await alertSuccess('Quiz submitted', `You earned ${totalScore} points!`);
      setExpandedQuizId(quiz.id);
      
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert('Failed to submit quiz. Please try again.');
    }
  };

  /**
   * Handle logout with confirmation
   */
  const handleLogout = () => {
    Swal.fire({
      title: 'Logout?',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    }).then((result) => {
      if (result.isConfirmed) {
        // Clear user data and redirect
        if (window.localStorage) window.localStorage.removeItem('hp_user');
        if (window.sessionStorage) window.sessionStorage.removeItem('hp_user');
        window.location.href = '/';
      }
    });
  };

  /**
   * Handle additional activity completion
   */
  const handleActivityCompletion = async (activity) => {
    // Check if already completed
    if (completedActivities.has(activity.id)) {
      Swal.fire({
        title: 'Already Completed',
        text: 'You have already claimed this activity.',
        icon: 'info',
        background: '#0f172a',
        color: '#e2e8f0',
        confirmButtonColor: '#22c55e'
      });
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Completion',
      text: `Did you really complete "${activity.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, I did!',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#22c55e',
      cancelButtonColor: '#6b7280'
    });

    if (result.isConfirmed) {
      try {
        // Submit completion to server
        const response = await fetch(`/api/activity-completions/${currentUser.code}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentId: currentUser.id,
            activityId: activity.id,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to complete activity');
        }

        const data = await response.json();

        // Mark as completed locally
        setCompletedActivities(prev => new Set([...prev, activity.id]));
        
        // Update user points locally
        const storedUser = localStorage.getItem('hp_user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            userData.points = (userData.points || 0) + activity.points;
            localStorage.setItem('hp_user', JSON.stringify(userData));
            setCurrentUser(userData);
          } catch (error) {
            console.error('Failed to update local user points:', error);
          }
        }

        // Show success message
        await alertSuccess('Activity Completed!', `You earned ${activity.points} points!`);
      } catch (error) {
        console.error('Failed to complete activity:', error);
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to complete activity. Please try again.',
          icon: 'error',
          background: '#0f172a',
          color: '#e2e8f0',
          confirmButtonColor: '#22c55e'
        });
      }
    }
  };

  /**
   * Handle word scramble submission
   */
  const handleScrambleSubmit = async (scramble) => {
    const answer = scrambleAnswers[scramble.id] || '';
    
    if (!answer.trim()) {
      Swal.fire({
        title: 'Missing Answer',
        text: 'Please enter your answer before submitting.',
        icon: 'warning',
        background: '#0f172a',
        color: '#e2e8f0',
        confirmButtonColor: '#22c55e'
      });
      return;
    }

    try {
      const response = await fetch(`/api/word-scramble-completions/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId: currentUser.id,
          scrambleId: scramble.id,
          answer: answer,
        }),
      });

      const data = await response.json();

      if (data.correct) {
        // Correct answer!
        setCompletedScrambles(prev => new Set([...prev, scramble.id]));
        
        // Update user points locally
        const storedUser = localStorage.getItem('hp_user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            userData.points = (userData.points || 0) + scramble.points;
            localStorage.setItem('hp_user', JSON.stringify(userData));
            setCurrentUser(userData);
          } catch (error) {
            console.error('Failed to update local user points:', error);
          }
        }

        await alertSuccess('Correct!', `You earned ${scramble.points} points!`);
      } else if (data.alreadyCompleted) {
        Swal.fire({
          title: 'Already Completed',
          text: 'You have already solved this word scramble.',
          icon: 'info',
          background: '#0f172a',
          color: '#e2e8f0',
          confirmButtonColor: '#22c55e'
        });
      } else if (data.incorrect) {
        // Wrong answer - show funny messages
        const funnyMessages = [
          { title: 'Are You Actually a Wizard? 🪄', text: 'Even a first-year could unscramble that! Maybe try reading the letters backwards... or forwards... or at all?' },
          { title: 'Confundus Charm Detected! 🌀', text: 'Did someone hit you with a Confundus Charm? That answer is more scrambled than the question!' },
          { title: 'Not Quite, Muggle! 🎩', text: "That's not it! Perhaps you need a quick trip to the library? Or maybe just a pair of glasses like Harry's?" },
          { title: 'Trolls Could Do Better! 👹', text: "Even a mountain troll could figure this out! And they're not known for their intelligence..." },
          { title: 'Did Peeves Distract You? 👻', text: 'Wrong answer! Were you paying attention, or were you too busy watching Peeves cause chaos?' },
          { title: 'Squib Energy Detected! 🔮', text: "Oh dear... That's not it. Perhaps a quick visit to Madam Pomfrey for a Brain-Boosting Potion?" },
        ];
        
        const randomMessage = funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
        
        Swal.fire({
          title: randomMessage.title,
          text: randomMessage.text,
          icon: 'error',
          background: '#0f172a',
          color: '#e2e8f0',
          confirmButtonColor: '#22c55e',
          confirmButtonText: 'Try Again!'
        });
      }
    } catch (error) {
      console.error('Failed to submit word scramble:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to submit answer. Please try again.',
        icon: 'error',
        background: '#0f172a',
        color: '#e2e8f0',
        confirmButtonColor: '#22c55e'
      });
    }
  };

  const isUserSorted = Boolean(currentUser?.house);

  return (
    <div style={styles.container}>
      {/* Logout Button */}
      <div style={styles.logoutContainer}>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
      </div>
      
      {/* Welcome Header */}
      <header style={styles.welcomeHeader}>
        <div style={styles.welcomeContent}>
          {currentUser?.name ? (
            <>
              <div style={styles.userName}>{currentUser.name}</div>
              <div style={styles.schoolWelcome}>
                welcome to {schoolInfo?.name || 'your school'}
              </div>
            </>
          ) : (
            <div style={styles.genericWelcome}>
              Welcome to {schoolInfo?.name || 'your school'}
            </div>
          )}
        </div>
      </header>

      {/* House Status and Sorting */}
      <section style={styles.houseSection}>
        {isUserSorted ? (
          <div style={styles.houseDisplay}>
            <div style={styles.houseInfo}>
              <img 
                src={`/images/houses/${currentUser.house.toLowerCase()}.png`} 
                alt={currentUser.house}
                style={styles.houseImage}
              />
              <span style={styles.houseName}>{currentUser.house}</span>
            </div>
            <div style={styles.housePointsDisplay}>
              <div style={styles.pointsLabel}>House Points Earned</div>
              <div style={styles.pointsValue}>
                {(currentUser.points || 0) + (currentUser.bonusPoints || 0)}
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.unsortedSection}>
            <h3 style={styles.unsortedTitle}>Your house has not been decided yet</h3>
            <button 
              onClick={() => navigate('/sorting-hat')} 
              style={styles.sortingButton}
            >
              Get Sorted
            </button>
          </div>
        )}
      </section>

      {/* Quizzes Section */}
      <section style={styles.quizzesSection}>
        <h3 style={styles.sectionTitle}>Quizzes</h3>
        {availableQuizzes.length === 0 ? (
          <div style={styles.emptyMessage}>
            No quizzes available yet. Your teacher will add them soon!
          </div>
        ) : (
          <div style={styles.quizList}>
            {availableQuizzes.map(quiz => {
            const hasSubmitted = Boolean(quizSubmissions[quiz.id]);
            const userAnswers = selectedAnswers[quiz.id] || {};
            const submittedAnswers = hasSubmitted ? quizSubmissions[quiz.id].answers : {};
            const isExpanded = expandedQuizId === quiz.id;
            const maxPoints = quiz.questions.reduce((sum, question) => sum + (question.points || 0), 0);

            return (
              <div key={quiz.id} style={styles.quizCard}>
                <div style={styles.quizHeader}>
                  <div style={styles.quizTitle}>{quiz.title}</div>
                  <div style={styles.quizMaxPoints}>
                    Max Points: {maxPoints}
                  </div>
                </div>

                <CollapsibleQuizSection isExpanded={isExpanded}>
                  <div style={styles.quizContent}>
                    {quiz.questions.map((question, questionIndex) => {
                      const selectedOptionIndex = hasSubmitted 
                        ? submittedAnswers[questionIndex] 
                        : userAnswers[questionIndex];
                      
                      return (
                        <div key={questionIndex} style={styles.questionContainer}>
                          <div style={styles.questionText}>{question.text}</div>
                          
                          <div style={styles.optionsContainer}>
                            {question.options.map((option, optionIndex) => {
                              const isSelected = selectedOptionIndex === optionIndex;
                              const isCorrect = hasSubmitted && optionIndex === question.correctIndex;
                              const isIncorrect = hasSubmitted && isSelected && selectedOptionIndex !== question.correctIndex;
                              
                              // Determine styling based on state
                              let optionStyle = { ...styles.optionCard };
                              
                              if (hasSubmitted) {
                                if (isCorrect) {
                                  optionStyle = { ...optionStyle, ...styles.correctOption };
                                } else if (isIncorrect) {
                                  optionStyle = { ...optionStyle, ...styles.incorrectOption };
                                } else {
                                  optionStyle = { ...optionStyle, ...styles.neutralOption };
                                }
                              } else if (isSelected) {
                                optionStyle = { ...optionStyle, ...styles.selectedOption };
                              } else {
                                optionStyle = { ...optionStyle, ...styles.unselectedOption };
                              }

                              return (
                                <div
                                  key={optionIndex}
                                  role="radio"
                                  aria-checked={isSelected}
                                  tabIndex={hasSubmitted ? -1 : 0}
                                  onKeyDown={(event) => {
                                    if (!hasSubmitted && (event.key === 'Enter' || event.key === ' ')) {
                                      event.preventDefault();
                                      selectQuizAnswer(quiz.id, questionIndex, optionIndex);
                                    }
                                  }}
                                  onClick={() => {
                                    if (!hasSubmitted) {
                                      selectQuizAnswer(quiz.id, questionIndex, optionIndex);
                                    }
                                  }}
                                  style={optionStyle}
                                >
                                  <div style={styles.optionText}>{option}</div>
                                  <div style={styles.optionIndicators}>
                                    {isCorrect && <span style={styles.correctIndicator}>✔</span>}
                                    {isIncorrect && <span style={styles.incorrectIndicator}>✘</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <div style={styles.questionPoints}>{question.points} pts</div>
                        </div>
                      );
                    })}

                    {/* Quiz Action Buttons */}
                    {hasSubmitted ? (
                      <div style={styles.submittedActions}>
                        <div style={styles.scoreDisplay}>
                          You scored {quizSubmissions[quiz.id].score} points.
                        </div>
                        <button 
                          onClick={() => setExpandedQuizId(null)} 
                          style={styles.collapseButton}
                        >
                          Collapse
                        </button>
                      </div>
                    ) : (
                      <div style={styles.activeQuizActions}>
                        <button 
                          onClick={() => submitQuiz(quiz)} 
                          style={styles.submitButton}
                        >
                          Submit Quiz
                        </button>
                        <button 
                          onClick={() => setExpandedQuizId(null)} 
                          style={styles.cancelButton}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </CollapsibleQuizSection>

                {/* Collapsed Quiz Actions */}
                {!isExpanded && (
                  <div style={styles.collapsedActions}>
                    {hasSubmitted ? (
                      <div style={styles.completedQuizInfo}>
                        <div style={styles.completedScore}>
                          You scored {quizSubmissions[quiz.id].score} points.
                        </div>
                        <button
                          onClick={() => setExpandedQuizId(quiz.id)}
                          style={styles.viewResultsButton}
                        >
                          View results
                        </button>
                      </div>
                    ) : (
                      isUserSorted ? (
                        <button
                          style={styles.takeQuizButton}
                          onClick={() => setExpandedQuizId(quiz.id)}
                        >
                          Take Quiz
                        </button>
                      ) : (
                        <div style={styles.sortingRequiredBadge}>
                          House Sorting Required
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </section>

      {/* Word Scrambles Section */}
      <section style={styles.wordScrambleSection}>
        <h3 style={styles.sectionTitle}>Word Scrambles</h3>
        {availableWordScrambles.length === 0 ? (
          <div style={styles.emptyMessage}>
            No word scrambles available yet. Your teacher will add them soon!
          </div>
        ) : (
          <div style={styles.scramblesList}>
            {availableWordScrambles.map(scramble => {
              const isCompleted = completedScrambles.has(scramble.id);
              const userAnswer = scrambleAnswers[scramble.id] || '';
              
              return (
                <div key={scramble.id} style={styles.scrambleCard}>
                  <div style={styles.scrambleTop}>
                    <div style={styles.scrambleWordDisplay}>{scramble.scrambledWord}</div>
                    <div style={styles.scramblePointsBadge}>+{scramble.points} pts</div>
                  </div>
                  
                  {!isUserSorted ? (
                    <div style={styles.sortingRequiredBadge}>
                      House Sorting Required
                    </div>
                  ) : isCompleted ? (
                    <div style={styles.scrambleCompleted}>
                      <span style={styles.completedIcon}>✓</span> Completed!
                    </div>
                  ) : (
                    <div style={styles.scrambleInputGroup}>
                      <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setScrambleAnswers({ ...scrambleAnswers, [scramble.id]: e.target.value.toUpperCase() })}
                        placeholder="Unscramble the word..."
                        style={styles.scrambleInput}
                        disabled={isCompleted}
                      />
                      <button
                        onClick={() => handleScrambleSubmit(scramble)}
                        style={styles.scrambleSubmitButton}
                        disabled={isCompleted}
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Additional Activities Section */}
      <section style={styles.rewardsSection}>
        <h3 style={styles.sectionTitle}>Additional Activities</h3>
        {availableActivities.length === 0 ? (
          <div style={styles.emptyMessage}>
            No additional activities available yet. Your teacher will add them soon!
          </div>
        ) : (
          <div style={styles.rewardsList}>
            {availableActivities.map(activity => {
              const isCompleted = completedActivities.has(activity.id);
              
              return (
                <div key={activity.id} style={styles.rewardCard}>
                  <div style={styles.rewardLeft}>
                    <div style={styles.rewardName}>{activity.name}</div>
                    <div style={styles.rewardPoints}>+{activity.points} points</div>
                  </div>
                  {isUserSorted ? (
                    <button
                      onClick={() => handleActivityCompletion(activity)}
                      style={{
                        ...styles.doneButton,
                        ...(isCompleted ? styles.doneButtonCompleted : {})
                      }}
                      disabled={isCompleted}
                    >
                      {isCompleted ? 'Completed!' : 'Done!'}
                    </button>
                  ) : (
                    <div style={styles.sortingRequiredBadge}>
                      House Sorting Required
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Game Code Footer */}
      <footer style={styles.gameCodeFooter}>
        Game Code: <strong>{currentUser?.code || 'No code found'}</strong>
      </footer>
    </div>
  );
}

// Consolidated styles for better organization and maintainability
const styles = {
  container: {
    display: 'grid',
    gap: 16,
  },
  
  logoutContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: -8,
  },
  
  logoutButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#a0b4ff',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  
  welcomeHeader: {
    margin: 0,
    textAlign: 'center',
    fontFamily: "'Cinzel Decorative', serif",
    fontSize: '1.5rem',
    fontWeight: 500,
  },
  
  welcomeContent: {
    fontFamily: "'Cinzel Decorative', serif",
    textAlign: 'center',
  },
  
  userName: {
    fontSize: '1.6rem',
    fontWeight: 700,
  },
  
  schoolWelcome: {
    fontSize: '1.1rem',
  },
  
  genericWelcome: {
    fontSize: '1.4rem',
  },
  
  houseSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  houseDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    width: '100%',
    justifyContent: 'space-between',
  },
  
  houseInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  
  houseImage: {
    width: 48,
    height: 48,
    objectFit: 'contain',
  },
  
  houseName: {
    fontWeight: 600,
    fontSize: '1.1rem',
  },
  
  housePointsDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  
  pointsLabel: {
    fontSize: '0.85rem',
    opacity: 0.8,
    fontStyle: 'italic',
  },
  
  pointsValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#eab308',
    fontFamily: "'Cinzel Decorative', serif",
    lineHeight: 1,
  },
  
  unsortedSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  unsortedTitle: {
    margin: 0,
  },
  
  sortingButton: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#eab308',
    color: '#0b0f1a',
    cursor: 'pointer',
    fontWeight: 700,
  },
  
  quizzesSection: {
    marginTop: 16,
  },
  
  sectionTitle: {
    margin: 0,
    marginBottom: 12,
  },
  
  rewardsSection: {
    marginTop: 16,
  },
  
  emptyMessage: {
    padding: '16px',
    textAlign: 'center',
    opacity: 0.7,
    fontStyle: 'italic',
    fontSize: '0.9rem',
  },
  
  sortingRequiredMessage: {
    padding: '16px',
    textAlign: 'center',
    opacity: 0.8,
    fontStyle: 'italic',
    fontSize: '0.9rem',
    color: '#fbbf24',
  },
  
  sortingRequiredBadge: {
    padding: '8px 12px',
    borderRadius: 6,
    background: 'transparent',
    color: '#fbbf24',
    fontSize: '0.85rem',
    fontWeight: 500,
    textAlign: 'center',
    border: '1px solid #78350f',
    fontStyle: 'italic',
    opacity: 0.85,
  },
  
  rewardsList: {
    display: 'grid',
    gap: 10,
  },
  
  rewardCard: {
    border: '1px solid #334155',
    borderRadius: 10,
    padding: '14px 16px',
    background: '#0f172a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  rewardLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  
  rewardName: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  
  rewardPoints: {
    color: '#eab308',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  
  doneButton: {
    padding: '8px 20px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#22c55e',
    color: '#0b0f1a',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.9rem',
    transition: 'background 150ms, opacity 150ms',
  },
  
  doneButtonCompleted: {
    background: '#64748b',
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  
  wordScrambleSection: {
    marginTop: 16,
  },
  
  scramblesList: {
    display: 'grid',
    gap: 10,
  },
  
  scrambleCard: {
    border: '1px solid #334155',
    borderRadius: 10,
    padding: '14px 16px',
    background: '#0f172a',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  
  scrambleTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  scrambleWordDisplay: {
    fontSize: '1.3rem',
    fontWeight: 700,
    letterSpacing: '3px',
    color: '#a78bfa',
    fontFamily: 'monospace',
  },
  
  scramblePointsBadge: {
    color: '#eab308',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  
  scrambleInputGroup: {
    display: 'flex',
    gap: 8,
  },
  
  scrambleInput: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    background: '#0b1220',
    color: '#fff',
    outline: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  
  scrambleSubmitButton: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#8b5cf6',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.9rem',
    transition: 'background 150ms',
  },
  
  scrambleCompleted: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#22c55e',
    fontWeight: 600,
    fontSize: '1rem',
  },
  
  completedIcon: {
    fontSize: '1.2rem',
  },
  
  quizList: {
    display: 'grid',
    gap: 12,
  },
  
  quizCard: {
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 12,
    background: '#0f172a',
    display: 'grid',
    gap: 8,
  },
  
  quizHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  quizTitle: {
    fontWeight: 600,
  },
  
  quizMaxPoints: {
    opacity: 0.7,
  },
  
  quizContent: {
    marginTop: 12,
  },
  
  questionContainer: {
    marginBottom: 12,
  },
  
  questionText: {
    fontWeight: 500,
    marginBottom: 6,
  },
  
  optionsContainer: {
    display: 'grid',
    gap: 8,
    marginTop: 6,
  },
  
  optionCard: {
    border: '1px solid #1f2937',
    borderRadius: 10,
    padding: '12px 14px',
    margin: '6px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'background 150ms, border-color 150ms, box-shadow 120ms',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    cursor: 'pointer',
  },
  
  unselectedOption: {
    background: '#060812',
    borderColor: '#1f2937',
  },
  
  selectedOption: {
    borderColor: '#60a5fa',
    background: '#071233',
    boxShadow: '0 6px 18px rgba(9, 30, 66, 0.45)',
  },
  
  correctOption: {
    borderColor: '#16a34a',
    background: '#052e14',
  },
  
  incorrectOption: {
    borderColor: '#dc2626',
    background: '#2b0a0a',
  },
  
  neutralOption: {
    borderColor: '#1f2937',
    background: '#060812',
  },
  
  optionText: {
    color: '#cbd5e1',
    fontSize: 14,
  },
  
  optionIndicators: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  
  correctIndicator: {
    color: '#16a34a',
    fontWeight: 700,
  },
  
  incorrectIndicator: {
    color: '#ef4444',
    fontWeight: 700,
  },
  
  questionPoints: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 4,
  },
  
  submittedActions: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  scoreDisplay: {
    fontStyle: 'italic',
    opacity: 0.8,
  },
  
  collapseButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#a0b4ff',
    cursor: 'pointer',
  },
  
  activeQuizActions: {
    marginTop: 12,
    display: 'flex',
    gap: 8,
  },
  
  submitButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#22c55e',
    color: '#0b0f1a',
    cursor: 'pointer',
    fontWeight: 600,
  },
  
  cancelButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#a0b4ff',
    cursor: 'pointer',
  },
  
  collapsedActions: {
    marginTop: 8,
  },
  
  completedQuizInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 13,
  },
  
  completedScore: {
    opacity: 0.85,
  },
  
  viewResultsButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#a0b4ff',
    cursor: 'pointer',
    fontSize: 13,
  },
  
  takeQuizButton: {
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#4f46e5',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.9rem',
    transition: 'background 150ms',
  },
  
  gameCodeFooter: {
    textAlign: 'center',
    fontStyle: 'italic',
    opacity: 0.8,
    marginTop: 12,
  },
};